/**
 * StaffPerformancePanel — NECS Admin · Operations/HR.
 * Manage the four performance factors per field staff member
 * (Time Performance · Job Completion · Safety Record · Attendance).
 * Each factor: Good / At Risk / Flagged + optional note. The combined
 * state drives the compliance trophy on the staff dashboards:
 * red = any Flagged · yellow = any At Risk · green = all Good.
 */
import { useCallback, useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../../lib/apiFetch';

const GOLD = '#C9A962';
const STATUS_COLORS = { good: '#22c55e', at_risk: '#f59e0b', violation: '#ef4444' };
const STATUS_LABELS = { good: 'Good standing', at_risk: 'At risk', violation: 'Active flag' };
const FACTOR_LABELS = {
  time_performance: 'Time Performance',
  job_completion: 'Job Completion',
  safety_record: 'Safety Record',
  attendance: 'Attendance',
};
const STATE_META = [
  ['good', 'Good', '#22c55e'],
  ['at_risk', 'At Risk', '#f59e0b'],
  ['flagged', 'Flagged', '#ef4444'],
];

function FactorRow({ staff, factorKey, onSave }) {
  const f = staff.factors[factorKey] || { state: 'good', note: '' };
  const [note, setNote] = useState(f.note || '');
  useEffect(() => { setNote(f.note || ''); }, [f.note]);
  return (
    <div className="rounded-lg border border-zinc-800 bg-black/30 p-3" data-testid={`perf-factor-${staff.user_id}-${factorKey}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs font-bold text-white">{FACTOR_LABELS[factorKey]}</p>
        <div className="flex gap-1">
          {STATE_META.map(([k, l, c]) => (
            <button key={k} onClick={() => onSave(factorKey, k, note)}
              className="px-2 py-1 rounded text-[9px] font-black uppercase tracking-wider border"
              style={f.state === k
                ? { backgroundColor: `${c}22`, borderColor: c, color: c }
                : { borderColor: '#3f3f46', color: '#71717a' }}
              data-testid={`perf-state-${staff.user_id}-${factorKey}-${k}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)}
        onBlur={() => { if ((note || '') !== (f.note || '')) onSave(factorKey, f.state, note); }}
        placeholder="Optional note…" maxLength={500}
        className="mt-2 w-full rounded bg-zinc-900 border border-zinc-800 px-2 py-1.5 text-[11px] text-zinc-300 focus:border-[#C9A962] focus:outline-none"
        data-testid={`perf-note-${staff.user_id}-${factorKey}`} />
      {f.updated_by && (
        <p className="text-[9px] text-zinc-600 mt-1">
          Updated by {f.updated_by} · {f.updated_at ? new Date(f.updated_at).toLocaleString() : ''}
        </p>
      )}
    </div>
  );
}

export default function StaffPerformancePanel() {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await apiFetch('/api/admin/staff-performance');
      setRows(d.staff || []);
    } catch (_) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (userId, factor, state, note) => {
    try {
      const d = await apiFetch(`/api/admin/staff-performance/${userId}`, {
        method: 'PUT', body: { factor, state, note },
      });
      setRows((rs) => rs.map((r) => (r.user_id === userId ? { ...r, factors: d.factors, status: d.status } : r)));
      toast.success('Performance factor saved');
      // Notify any staff-facing trophy on this device to refetch immediately.
      try { window.dispatchEvent(new CustomEvent('necs-compliance-changed', { detail: { user_id: userId } })); } catch (_) { /* noop */ }
    } catch (e) { toast.error(e.detail || 'Save failed'); }
  };

  return (
    <Card className="bg-zinc-900 border-l-4 border-l-[#C9A962] p-5" data-testid="staff-performance-panel">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="h-5 w-5" style={{ color: GOLD }} />
        <h3 className="text-white font-bold text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          STAFF PERFORMANCE · COMPLIANCE TROPHY
        </h3>
      </div>
      <p className="text-[11px] text-zinc-500 mb-4">
        Four factors per staff member drive the trophy on their dashboard — red if any factor is Flagged, yellow if any is At Risk, green when all four are Good.
      </p>
      <div className="space-y-2">
        {rows.length === 0 && <p className="text-xs text-zinc-600 py-4 text-center">No field staff found.</p>}
        {rows.map((s) => {
          const open = openId === s.user_id;
          return (
            <div key={s.user_id} className="rounded-xl border border-zinc-800" data-testid={`perf-staff-${s.user_id}`}>
              <button onClick={() => setOpenId(open ? null : s.user_id)}
                className="w-full flex items-center justify-between gap-2 p-3.5"
                data-testid={`perf-staff-toggle-${s.user_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <Trophy className="h-5 w-5 shrink-0" style={{ color: STATUS_COLORS[s.status] }}
                    data-testid={`perf-trophy-${s.user_id}`} data-status={s.status} />
                  <div className="text-left min-w-0">
                    <p className="text-sm font-bold text-white break-words">{s.name}</p>
                    <p className="text-[10px] text-zinc-500 capitalize">{(s.role || '').replace(/_/g, ' ')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[9px] font-black uppercase tracking-wider" style={{ color: STATUS_COLORS[s.status] }}>
                    {STATUS_LABELS[s.status]}
                  </span>
                  {open ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
                </div>
              </button>
              {open && (
                <div className="px-3.5 pb-3.5 grid md:grid-cols-2 gap-2">
                  {Object.keys(FACTOR_LABELS).map((k) => (
                    <FactorRow key={k} staff={s} factorKey={k}
                      onSave={(factor, state, note) => save(s.user_id, factor, state, note)} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
